/**
 * Toolbar Component for Heat Pump Simulation
 * Provides controls for playback, visualization toggles, and information access
 */

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Space } from "antd";
import {
  PauseOutlined,
  CaretRightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import { TutorialQuests } from "./TutorialQuests";

/**
 * Simulation Control Toolbar
 * Contains play/pause, visualization toggles, and settings
 */
export const SimulationToolbar = ({
  isPlaying,
  onPlayPause,
  showRadiatorRating,
  onToggleRadiatorRating,
  showRadiatorRatingTool = true,
  showHeatFlux,
  onToggleHeatFlux,
  showHeatFluxTool = true,
  onOpenResources,
  headerVisible,
  onToggleHeader,
  showResourcesButton = true,
  showHeaderToggle = true,
  // Tutorial props
  tutorialProps,
}) => {
  const { t } = useTranslation();
  
  return (
    <Space direction="horizontal" size={10} style={{ background: "transparent", width: '100%', display: 'flex', alignItems: 'center' }} data-testid="simulation-toolbar">
      {/* Left Group: Playback Controls and Visualization Toggles */}
      <Space direction="horizontal" size="small" style={{ flex: '0 0 auto' }} data-testid="simulation-toolbar-primary-actions">
        <Button
          type={isPlaying ? "primary" : "default"}
          size="large"
          icon={isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
          onClick={onPlayPause}
          title={isPlaying ? t('heatPumpSimulation.controls.pauseSimulation') : t('heatPumpSimulation.controls.playSimulation')}
          data-testid="simulation-run-btn"
          style={{ width: "40px", height: "40px", padding: "0px" }}
        />

        {showRadiatorRatingTool && (
          <Button
            type={showRadiatorRating ? "primary" : "default"}
            size="large"
            icon={
              <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
                {/* Radiator body - 5 sections with detailed fins */}
                <rect x="3" y="15" width="4" height="16" rx="0.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <rect x="3.5" y="16" width="3" height="14" rx="0.3" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <rect x="9" y="15" width="4" height="16" rx="0.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <rect x="9.5" y="16" width="3" height="14" rx="0.3" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <rect x="15" y="15" width="4" height="16" rx="0.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <rect x="15.5" y="16" width="3" height="14" rx="0.3" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <rect x="21" y="15" width="4" height="16" rx="0.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <rect x="21.5" y="16" width="3" height="14" rx="0.3" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <rect x="27" y="15" width="4" height="16" rx="0.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <rect x="27.5" y="16" width="3" height="14" rx="0.3" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <line x1="3" y1="15" x2="31" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="3" y1="31" x2="31" y2="31" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M17 3 L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M14 7 L17 3 L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            }
            onClick={onToggleRadiatorRating}
            title={t('heatPumpSimulation.controls.toggleRadiatorRating')}
            data-testid="radiator-rating-btn"
            style={{ width: "60px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}
          />
        )}

        {showHeatFluxTool && (
          <Button
            type={showHeatFlux ? "primary" : "default"}
            size="large"
            icon={
              <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                <rect x="13" y="13" width="14" height="14" rx="1.5" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M3 20 L11 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                <path d="M5.5 16.5 L3 20 L5.5 23.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M37 20 L29 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                <path d="M34.5 16.5 L37 20 L34.5 23.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <circle cx="16.5" cy="20" r="2" fill="currentColor" opacity="0.6"/>
                <circle cx="23.5" cy="20" r="2" fill="currentColor" opacity="0.6"/>
              </svg>
            }
            onClick={onToggleHeatFlux}
            title={t('heatPumpSimulation.controls.toggleHeatFlux')}
            data-testid="heat-flux-btn"
            style={{ width: "56px", height: "40px", padding: "0px", display: "flex", alignItems: "center", justifyContent: "center" }}
          />
        )}

        {showResourcesButton && (
          <Button
            size="large"
            icon={<FilePdfOutlined style={{ fontSize: "24px" }} />}
            onClick={onOpenResources}
            title={t('heatPumpSimulation.controls.learningResources')}
            data-testid="learning-resources-btn"
            style={{ width: "40px", height: "40px", padding: "0px", color: "#ff4d4f" }}
          />
        )}

        {showHeaderToggle && (
          <Button
            size="large"
            icon={headerVisible ? <FullscreenOutlined  style={{ fontSize: "24px" }} /> : <FullscreenExitOutlined  style={{ fontSize: "24px" }} />}
            onClick={onToggleHeader}
            title={headerVisible ? t('heatPumpSimulation.controls.hideHeader') : t('heatPumpSimulation.controls.showHeader')}
            data-testid="toggle-header-btn"
            style={{ width: "40px", height: "40px", padding: "0px" }}
          />
        )}
      </Space>

      <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
        {tutorialProps && <TutorialQuests {...tutorialProps} />}
      </div>
    </Space>
  );
};

SimulationToolbar.propTypes = {
  isPlaying: PropTypes.bool.isRequired,
  onPlayPause: PropTypes.func.isRequired,
  showRadiatorRating: PropTypes.bool.isRequired,
  onToggleRadiatorRating: PropTypes.func.isRequired,
  showRadiatorRatingTool: PropTypes.bool,
  showHeatFlux: PropTypes.bool.isRequired,
  onToggleHeatFlux: PropTypes.func.isRequired,
  showHeatFluxTool: PropTypes.bool,
  onOpenResources: PropTypes.func.isRequired,
  headerVisible: PropTypes.bool.isRequired,
  onToggleHeader: PropTypes.func.isRequired,
  showResourcesButton: PropTypes.bool,
  showHeaderToggle: PropTypes.bool,
  tutorialProps: PropTypes.object,
};
